import {
  useContext,
  useEffect,
  useState,
  ChangeEvent,
  useRef,
  lazy,
  Suspense,
  forwardRef,
  useCallback,
  useImperativeHandle,
} from 'react';
import { useForm } from 'react-hook-form';
import { InputGroup } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useNavigate, useLocation, Link } from 'react-router-dom';

import { PromptContext } from '../../context/PromptContext';
import { LoadingContext } from '../../context/LoadingContext';
import { AppContext } from '../../context/AppContext';
import icon_loading from '../../assets/elysia-loader.gif';
import DocumentIcon from '../../assets/icon-google-document.svg?react';
import SubmitPromptIcon from '../../assets/icon-send.svg?react';
import StopPromptIcon from '../../assets/icon-google-stop.svg?react';
import PrivateChatIcon from '../../assets/icon-private-chat.svg?react';
import CircleLoadingIcon from '../../assets/icon-circle-loading.svg?react';
import ChatNavItems from '../home/ChatNavItems';
import { HISTORY_ZERO_STATE } from '../../constants/HistoryConstants';
import { generateChatSessionId, getNotificationMessage } from '../../utils/chat';
import ToolIconLeft from '@/assets/icon-tool-left.svg?react';
import ToolIconRight from '@/assets/icon-tool-right.svg?react';
import FinanceAppIcon from '@/assets/icon-finance-app.svg?react';

import {
  FILE_UPLOAD_FAIL_ID,
  FILE_UPLOAD_TYPE_ID,
  FILE_UPLOAD_ERROR_ID,
  FILE_UPLOAD_ACCEPTED_ID,
  FILE_UPLOAD_SIZE_ID,
  PROMPT_WORD_LENGTH,
  CHAT_DISABLED_ID,
  NEGATIVE_CHAR_LENGTH,
} from '../../config/settings';
import notify from '../../services/notify';
import { ChatSavedPrompt } from './ChatSavedPrompt';
const ChatWithDoc = lazy(() => import('../home/ChatWithDoc'));
import { ChatWidgets } from './ChatWidgets';
import { toast } from 'react-toastify';
import ModelSelect from './ModelSelect';
import LanguageSelect from './LanguageSelect';
import ModelSelectIcon from '../../assets/icon-change-mdel.svg?react';
import LanguageSelectIcon from '../../assets/icon-language.svg?react';
import { SettingContext } from '@/context/SettingContext';
import BrandAgentModal from '../brand-agent/BrandAgentModal';
import useElysiaStore from '@/app/elysia-store';
import ToolsDropdown from './ToolsDropdown';
import AddResourceDropdown from './AddResourceDropdown';
import SettingDropdown from './SettingDropdown';
import PromptHelper from './PromptHelper';
import DisabledWrapper from '../common/UI/DisabledWrapper';
import ConfigureImageModal from '../app-builder/ConfigureImageModal';
import FileUploadBadge from '../common/UI/FileUploadBadge';
import ConfigureFinanceAppModal from '../app-builder/ConfigureFinanceAppModal';
import { useAttachedFiles } from '@/hooks/useAttachedFiles';
import OverlayTooltip from '../common/UI/OverlayTooltip';
import ExcelCsvGuidelinesModal from '../common/ExcelCsvGuidelinesModal';
import { AuthContext } from '@/context/AuthContext';

const ChatInput = forwardRef((_, ref) => {
  const { register } = useForm();
  const { setPrompt, setProcessed } = useContext(PromptContext);

  const sources = useElysiaStore((state) => state.sources);
  const promptInput = useElysiaStore((state) => state.promptInput);
  const chatAgent = useElysiaStore((state) => state.chatAgent);
  const clearState = useElysiaStore((state) => state.clearState);
  const newChat = useElysiaStore((state) => state.newChat);
  const isStreamingRequested = useElysiaStore((state) => state.isStreamingRequested);
  const isPrivateChat = useElysiaStore((state) => state.isPrivateChat);
  const { profileInfo } = useContext(AuthContext);
  const wordsCount = useElysiaStore((state) => state.wordsCount) || 0;
  const wordCountError = useElysiaStore((state) => state.wordCountError) || false;
  const showFileToasts = useElysiaStore((state) => state.showFileToasts);
  const isChatDisabled = useElysiaStore((state) => state.isChatDisabled);
  const isPromptSetManually = useElysiaStore((state) => state.isPromptSetManually);
  const isToolsButtonSelected = useElysiaStore((state) => state.isToolsButtonSelected);
  const activeChatHistoryRecord = useElysiaStore((state) => state.activeChatHistoryRecord);
  const profitCenter = useElysiaStore((state) => state.profitCenter);
  const financeAppLocation = useElysiaStore((state) => state.financeAppLocation);
  const isChatActive = useElysiaStore((state) => state.isChatActive);
  const appKey = useElysiaStore((state) => state.appKey);

  const setUploadedFile = useElysiaStore.getState().setUploadedFile;
  const setUploadedFileTitle = useElysiaStore.getState().setUploadedFileTitle;
  const setIsChatActive = useElysiaStore.getState().setIsChatActive;
  const setPromptInput = useElysiaStore.getState().setPromptInput;
  const setChatAgent = useElysiaStore.getState().setChatAgent;
  const setEntryPoint = useElysiaStore.getState().setEntryPoint;
  const setClearState = useElysiaStore.getState().setClearState;
  const setActiveChatHistoryRecord = useElysiaStore.getState().setActiveChatHistoryRecord;
  const setSessionPath = useElysiaStore.getState().setSessionPath;
  const setNewChat = useElysiaStore.getState().setNewChat;
  const setChatSessionId = useElysiaStore.getState().setChatSessionId;
  const setIsRestrictedChatSessionStatus =
    useElysiaStore.getState().setIsRestrictedChatSessionStatus;
  const setIsRestrictedAppStatus = useElysiaStore.getState().setIsRestrictedAppStatus;
  const setIsStreamingRequested = useElysiaStore.getState().setIsStreamingRequested;
  const setIsPrivateChat = useElysiaStore.getState().setIsPrivateChat;
  const setShowScrollIcon = useElysiaStore.getState().setShowScrollIcon;
  const setWordsCount = useElysiaStore.getState().setWordsCount;
  const setWordCountError = useElysiaStore.getState().setWordCountError;
  const setShowFileToasts = useElysiaStore.getState().setShowFileToasts;
  const setIsChatDisabled = useElysiaStore.getState().setIsChatDisabled;
  const setIsAddSourcesActive = useElysiaStore.getState().setIsAddSourcesActive;
  const setCategoryCount = useElysiaStore.getState().setCategoryCount;
  const setSelectedSources = useElysiaStore.getState().setSelectedSources;
  const setSources = useElysiaStore.getState().setSources;
  const brandDoc = useElysiaStore((state) => state.brandDoc);
  const isPreview = useElysiaStore((state) => state.isPreview);
  const isAppDisabled = useElysiaStore((state) => state.isAppDisabled);
  const isAppPreview = useElysiaStore((state) => state.isAppPreview);
  const greetings = useElysiaStore((state) => state.greetings);
  const name = useElysiaStore((state) => state.name);
  const promptToolsConfig = useElysiaStore((state) => state.promptToolsConfig);
  const isAppTerminated = useElysiaStore((state) => state.isAppTerminated);
  const appFileBadgeTitle = useElysiaStore((state) => state.appFileBadgeTitle);
  const isAppFileAttached = useElysiaStore((state) => state.isAppFileAttached);
  const setBrandDoc = useElysiaStore.getState().setBrandDoc;
  // for async payload check
  const division = useElysiaStore((state) => state.division);
  const brand = useElysiaStore((state) => state.brand);
  const brandDetails = useElysiaStore((state) => state.brandDetails);
  const persona = useElysiaStore((state) => state.persona);
  const aiAppId = useElysiaStore((state) => state.aiAppId);

  const { isTouchDevice } = useContext(AppContext);
  const {
    selectedModel,
    selectedLanguage,
    defaultLanguage,
    defaultModel,
    languageUsedForResponse,
    modelUsedForResponse,
    availableImageQuota,
    setSelectedLanguage,
    setSelectedModel,
  } = useContext(SettingContext);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  if (window.location.pathname === '/' && queryParams.get('private-chat') === 'true') {
    setIsPrivateChat?.(true);
  }
  const { isLoading } = useContext(LoadingContext);
  const [placeholderText, setPlaceHolderText] = useState('');
  const [isPromptError, setIsPromptError] = useState<boolean>(false);
  const [showDocPopup, setShowDocPopup] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [initiatedSavedPrompts, setInitiatedSavedPrompts] = useState(false);

  const textareaRef = useRef(document.createElement('textarea'));
  const textareaParent = useRef<HTMLDivElement | null>(null);
  // const fileInputRef = useRef<HTMLInputElement>(null);
  const maxPromptHeight = 13 * 22; // show 12 lines of prompt before scrolling. 13 used to account for padding at the top. 22 is line height;
  const inputTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [initiatedModelSelect, setInitiatedModelSelect] = useState(false);
  const [initiatedLanguageSelect, setInitiatedLanguageSelect] = useState(false);
  const [initiatedBrandAgentSelect, setInitiatedBrandAgentSelect] = useState(false);
  const [initiatedPromptHelper, setInitiatedPromptHelper] = useState(false);
  const originalAgentRef = useRef<ChatAgentType | null>(null);
  const originalAppIdRef = useRef<string | null>(null);
  const [initiatedImageGenModal, setInitiatedImageGenModal] = useState(false);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [initiatedFinanceApp, setInitiatedFinanceApp] = useState(false);
  const setAttachedFileArray = useElysiaStore((state) => state.setAttachedFileArray);
  const setPendingMessageFiles = useElysiaStore.getState().setPendingMessageFiles;
  const setChatInputSources = useElysiaStore.getState().setChatInputSources;

  const isPayloadReady =
    profileInfo?.userId != null &&
    selectedModel?.model != null &&
    selectedModel?.name_of_model != null &&
    defaultModel?.name_of_model != null &&
    selectedLanguage != null &&
    defaultLanguage != null &&
    (appKey !== 'brand_agent' ||
      (division != null &&
        brand?.name != null &&
        brandDetails?.id != null &&
        (isPreview || persona?.id != null))) &&
    // check for apps
    (chatAgent !== 'app-agent' || isAppPreview || aiAppId != null) &&
    (appKey !== 'finance' || (profitCenter?.label != null && financeAppLocation?.label != null));

  const {
    fileInputRef,
    displaySources,
    isAnyFileUploading,
    handleFileSelect,
    handleAttachFileClick,
    handleRemoveFile,
    handlePaste,
    removeFilesFromChatInput,
  } = useAttachedFiles();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Set the initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(
    () => {
      if (activeChatHistoryRecord && activeChatHistoryRecord?.history[0]?.references?.length > 0) {
        setIsAddSourcesActive?.(true);
      }
    },
    // eslint-disable-next-line
    [activeChatHistoryRecord]
  );

  useEffect(() => {
    switch (chatAgent) {
      case 'Document': {
        setPlaceHolderText(ChatNavItems[0].placeHolderText);
        break;
      }
      case 'app-agent': {
        setPlaceHolderText(greetings);
        break;
      }
      default: {
        setBrandDoc(false);
        setPlaceHolderText('Ask Elysia anything...');
        break;
      }
    }
    //eslint-disable-next-line
  }, [chatAgent, isPrivateChat, greetings]);

  useEffect(() => {
    textareaRef.current.style.height = 'auto';

    if (textareaRef.current.scrollHeight > maxPromptHeight) {
      textareaRef.current.style.height = `${maxPromptHeight}px`;
      textareaRef.current.style.overflowY = 'auto';
    } else {
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      textareaRef.current.style.overflowY = 'hidden';
    }
    //eslint-disable-next-line
  }, [promptInput.length]);

  useEffect(() => {
    if (!isTouchDevice && !isAppPreview) {
      textareaRef.current.focus();
      if (textareaParent.current) {
        textareaParent.current.focus();
      }
    }

    if (isChatDisabled) {
      textareaRef.current.blur();
      // Clear any existing timeout to prevent multiple toasts
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
      toastTimeoutRef.current = setTimeout(() => {
        notify({
          message: getNotificationMessage(chatAgent, availableImageQuota, name, isAppFileAttached),
          type: 'error',
          autoClose: false,
          toastId: CHAT_DISABLED_ID,
        });
      }, 10);
    } else {
      // Clear any pending timeout
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
        toastTimeoutRef.current = null;
      }
      toast.dismiss(CHAT_DISABLED_ID);
    }
    //eslint-disable-next-line
  }, [clearState, newChat, isChatDisabled, chatAgent, availableImageQuota, name, isAppPreview]);

  useEffect(() => {
    if (!isPromptSetManually) {
      setPromptInput('');
    }
    // eslint-disable-next-line
  }, [activeChatHistoryRecord?.session_id]);

  useEffect(() => {
    if (chatAgent === 'Generic') {
      setIsAddSourcesActive?.(false);
    }
    if (
      chatAgent === 'app-agent' &&
      appKey === 'image_generator' &&
      availableImageQuota !== null &&
      availableImageQuota <= 0
    ) {
      setIsChatDisabled?.(true);
    }
    // eslint-disable-next-line
  }, [chatAgent, availableImageQuota, appKey]);

  useEffect(() => {
    if (showFileToasts === false) {
      toast.dismiss(FILE_UPLOAD_ERROR_ID);
      toast.dismiss(FILE_UPLOAD_ACCEPTED_ID);
      toast.dismiss(FILE_UPLOAD_TYPE_ID);
      toast.dismiss(FILE_UPLOAD_FAIL_ID);
      toast.dismiss(FILE_UPLOAD_SIZE_ID);
    }
  }, [showFileToasts]);

  useEffect(() => {
    if (inputTimeoutRef.current) {
      clearTimeout(inputTimeoutRef.current);
    }

    const trimmedInput = promptInput.trim();
    if (!trimmedInput) {
      setWordsCount?.(0);
      setWordCountError?.(false);
      return;
    }

    if (appKey === 'image_generator' && chatAgent === 'app-agent') {
      const chars = trimmedInput.split('').filter(Boolean);
      setWordsCount?.(chars.length);
      if (chars.length > NEGATIVE_CHAR_LENGTH) {
        setIsPromptError(true);
        setWordCountError?.(true);
      } else {
        setWordCountError?.(false);
        setIsPromptError(false);
      }
    } else {
      inputTimeoutRef.current = setTimeout(() => {
        const words = trimmedInput.split(/[\s,]+/).filter((word) => word.length > 0);
        setWordsCount?.(words.length);
        if (words.length > PROMPT_WORD_LENGTH) {
          setIsPromptError(true);
          setWordCountError?.(true);
        } else {
          setWordCountError?.(false);
          setIsPromptError(false);
        }
      }, 300);
    }

    // eslint-disable-next-line
  }, [promptInput]);

  const stopStreaming = () => {
    useElysiaStore.setState({ isStreamingStopped: true });
    setIsStreamingRequested?.(false);
  };

  const submitPrompt = () => {
    if (promptInput) {
      if (appKey === 'image_generator' && wordsCount > NEGATIVE_CHAR_LENGTH) return;
      if (wordsCount <= PROMPT_WORD_LENGTH) {
        if ((chatAgent === 'Document' || brandDoc) && sources?.length === 0) {
          notify({
            message: 'Please select a document to proceed.',
            type: 'error',
          });
        } else {
          const filesForThisMessage = displaySources.map((source) => source.name);

          setIsPromptError(false);
          setClearState?.(false);
          setEntryPoint?.('prompt');
          setPendingMessageFiles?.(filesForThisMessage);
          setPrompt?.(promptInput.trim());
          setProcessed?.(false);
          setPromptInput('');
          setWordsCount?.(0);
          removeFilesFromChatInput(filesForThisMessage);
        }
        languageUsedForResponse.current = selectedLanguage;
        modelUsedForResponse.current = selectedModel;
      } else {
        setIsPromptError(true);
      }
    } else {
      console.error('Please enter your question in the box provided.');
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter' && !isAnyFileUploading) {
      // for a touch enabled device, enter will add a new line and not submit the prompt
      //TODO: Need refining for external keyboard and hybrid devices
      if (isTouchDevice) {
        return;
      }
      if (e.shiftKey) {
        return;
      }
      e.preventDefault();
      if (!isLoading) {
        submitPrompt();
      }
    }
  };

  const createNewSession = () => {
    setClearState?.(true);
    setNewChat?.(true);
    setChatSessionId?.(generateChatSessionId());
    setActiveChatHistoryRecord(HISTORY_ZERO_STATE);
    if (!isPreview && !isAppPreview) {
      navigate({
        pathname: '/',
        search: queryParams.toString(),
      });
    }
    setIsChatActive?.(false);
    setPromptInput('');
    setSessionPath?.('');
    setIsRestrictedChatSessionStatus?.('');
    setIsRestrictedAppStatus('');
    setWordsCount?.(0);
    setWordCountError?.(false);
  };

  const handleAddSources = () => {
    setShowDocPopup(true);
  };

  const togglePrivateChat = useCallback(() => {
    createNewSession();
    if (!isPrivateChat) {
      if (!originalAgentRef.current && chatAgent === 'app-agent') {
        originalAgentRef.current = chatAgent;
      }
      if (!originalAppIdRef.current) originalAppIdRef.current = queryParams.get('app');
      if (chatAgent !== 'app-agent') setChatAgent?.('Generic');

      queryParams.set('private-chat', 'true');
    } else {
      // exiting private chat
      if (!isAppTerminated) {
        if (originalAgentRef.current) setChatAgent?.(originalAgentRef.current);
        if (originalAppIdRef.current) queryParams.set('app', originalAppIdRef.current);
      }

      queryParams.delete('private-chat');
      originalAgentRef.current = null;
      originalAppIdRef.current = null;
    }

    navigate({
      pathname: '/',
      search: queryParams.toString(),
    });
    if (chatAgent !== 'app-agent') setSelectedModel(defaultModel);
    setSelectedLanguage(defaultLanguage);
    setIsPrivateChat?.(!isPrivateChat);
    setShowScrollIcon?.(false);
    setUploadedFileTitle?.('');
    setUploadedFile?.(null);
    setAttachedFileArray?.([]);
    setChatInputSources?.([]);
    setShowFileToasts?.(false);
    if (!appFileBadgeTitle) {
      setSelectedSources?.([]);
      setSources?.([]);
      setCategoryCount({});
    }
    if (chatAgent === 'app-agent' && !appFileBadgeTitle && brandDoc) setBrandDoc(false);
    if (chatAgent !== 'app-agent') setChatAgent?.('Generic');

    //eslint-disable-next-line
  }, [
    isPrivateChat,
    chatAgent,
    isAppTerminated,
    queryParams,
    navigate,
    setChatAgent,
    setCategoryCount,
    setSelectedModel,
    setSelectedLanguage,
    setIsPrivateChat,
    setShowScrollIcon,
    setUploadedFileTitle,
    setUploadedFile,
    setAttachedFileArray,
    setShowFileToasts,
    defaultModel,
    defaultLanguage,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      togglePrivateChat,
    }),
    [togglePrivateChat]
  );
  return (
    <>
      <div className="chat-input">
        <div aria-live="polite" className="sr-only">
          {isLoading ? 'Fetching results, please wait' : ''}
        </div>
        {isLoading && (
          <div className="chat-input-loading">
            <img src={icon_loading} alt="loading" />
          </div>
        )}

        <div className="w-100">
          <InputGroup
            className={`chat-input-group ${isPrivateChat ? 'private-chat' : ''} ${isMobile ? 'is-mobile' : ''} `}
          >
            {name !== 'Image Generator' && (
              <ChatWidgets
                OnTogglePrivateChat={togglePrivateChat}
                onAddSources={handleAddSources}
                onInitiatedSavedPrompts={setInitiatedSavedPrompts}
                onAttachFileClick={handleAttachFileClick}
                onInitiatedModelSelect={setInitiatedModelSelect}
                onInititatedLanguageSelect={setInitiatedLanguageSelect}
                onInitiatedBrandAgentSelect={setInitiatedBrandAgentSelect}
                onInitiatedPromptHelper={setInitiatedPromptHelper}
                onoInitiatedFinanceModal={setInitiatedFinanceApp}
              />
            )}
            {appKey === 'image_generator' && chatAgent === 'app-agent' && (
              <Button
                className={`tool-icon-btn d-block d-md-none chat-widgets ms-2 ${isToolsButtonSelected ? 'selected' : ''}`}
                onClick={() => setInitiatedImageGenModal(true)}
                aria-label="Tools button"
              >
                <div className="d-flex justify-content-center align-items-center gap-1">
                  <ToolIconLeft />
                  <ToolIconRight />
                </div>
              </Button>
            )}
            <div
              className={`text-area-container ${isChatDisabled ? 'disable-prompt' : ''} ${isPrivateChat ? 'border-dashed' : 'border-solid'} `}
              ref={textareaParent}
            >
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                multiple
                accept=".txt,.pdf,.ppt,.pptx,.doc,.docx,.csv,.xls,.xlsx,.png,.jpg,.jpeg"
              />
              {displaySources.length > 0 && (
                <div className="uploaded-files-list">
                  {displaySources.map((source) => (
                    <FileUploadBadge
                      key={source.name}
                      uploadedFileTitle={source.title}
                      statusText={source.status}
                      iconState={source.iconState}
                      showRemoveButton={appFileBadgeTitle ? false : true}
                      onRemove={() => handleRemoveFile(source.name)}
                    />
                  ))}
                </div>
              )}
              {appFileBadgeTitle && (
                <div className="files-container">
                  <FileUploadBadge uploadedFileTitle={appFileBadgeTitle} showRemoveButton={false} />
                </div>
              )}
              <Form.Control
                {...register('chat-input')}
                className={`bg-transparent w-100 ${chatAgent === 'Document' ? 'chat-with-doc' : ''} promt-input`}
                aria-label={placeholderText || 'Chat-prompt-input'}
                placeholder={placeholderText}
                name="chat-input"
                onPaste={handlePaste}
                as="textarea"
                data-testid="chat-prompt-input"
                rows={1}
                value={promptInput}
                ref={textareaRef}
                onKeyDown={handleKeyDown}
                onInput={(e: ChangeEvent<HTMLTextAreaElement>) => setPromptInput(e.target?.value)}
                id="prompt-input"
                autoComplete="off"
                disabled={isChatDisabled || (isAppPreview && isAppDisabled)}
              />
              <div
                className="d-flex justify-content-between align-items-center w-100 promt-toolbar-container"
                data-testid="prompt-toolbar-container"
              >
                <DisabledWrapper disabled={isAppDisabled}>
                  <div className="d-flex gap-2">
                    {promptToolsConfig.chatWithDocuments && (
                      <AddResourceDropdown
                        onAddSources={handleAddSources}
                        onAttachFileClick={handleAttachFileClick}
                      />
                    )}
                    {!isPrivateChat && promptToolsConfig.tools && (
                      <ToolsDropdown
                        onInitiatedSavedPrompts={setInitiatedSavedPrompts}
                        onInitiatedPromptHelper={setInitiatedPromptHelper}
                      />
                    )}
                    {(showDocPopup || isAnyFileUploading) && (
                      <Suspense>
                        <ChatWithDoc
                          setShowDocPopup={setShowDocPopup}
                          showDocPopup={showDocPopup}
                        />
                      </Suspense>
                    )}
                    <Suspense>
                      <ExcelCsvGuidelinesModal />
                    </Suspense>
                    {promptToolsConfig.settings && (
                      <SettingDropdown
                        onInitiatedModelSelect={setInitiatedModelSelect}
                        onInititatedLanguageSelect={setInitiatedLanguageSelect}
                      />
                    )}
                    {promptToolsConfig.brandAgent && (
                      <BrandAgentModal
                        initiated={initiatedBrandAgentSelect}
                        setInitiated={setInitiatedBrandAgentSelect}
                      />
                    )}
                    {promptToolsConfig.configImage && (
                      <Button
                        className={`tool-icon ${isToolsButtonSelected ? 'selected' : ''}`}
                        onClick={() => setInitiatedImageGenModal(true)}
                        aria-label="Tools button"
                      >
                        <div className="d-flex justify-content-center align-items-center gap-1">
                          <ToolIconLeft />
                          <ToolIconRight />
                        </div>
                      </Button>
                    )}
                    <ChatSavedPrompt
                      initiated={initiatedSavedPrompts}
                      setInitiated={setInitiatedSavedPrompts}
                    />
                    <ModelSelect
                      initiated={initiatedModelSelect}
                      setInitiated={setInitiatedModelSelect}
                    />
                    <LanguageSelect
                      initiated={initiatedLanguageSelect}
                      setInitiated={setInitiatedLanguageSelect}
                    />
                    <PromptHelper
                      initiated={initiatedPromptHelper}
                      setInitiated={setInitiatedPromptHelper}
                    />
                    <ConfigureImageModal
                      initiated={initiatedImageGenModal}
                      setInitiated={setInitiatedImageGenModal}
                    />

                    {promptToolsConfig?.configFinance && (
                      <ConfigureFinanceAppModal
                        initiated={initiatedFinanceApp}
                        setInitiated={setInitiatedFinanceApp}
                      />
                    )}
                  </div>
                </DisabledWrapper>
                <div>
                  {isStreamingRequested ? (
                    <Button
                      aria-label="Stop Streaming"
                      className="stop-button prompt-input-button active"
                      data-testid="stop-streaming"
                      onClick={stopStreaming}
                    >
                      <StopPromptIcon />
                    </Button>
                  ) : (
                    <>
                      <span
                        className={`word-count mx-4 ${wordCountError ? 'word-count-error' : ''}`}
                        data-testid="word-count"
                      >
                        {wordsCount}/
                        {appKey === 'image_generator' ? NEGATIVE_CHAR_LENGTH : PROMPT_WORD_LENGTH}
                      </span>
                      {isAnyFileUploading ? (
                        <OverlayTooltip
                          tooltipText="Files are uploading. You can send once they're ready."
                          buttonContent={
                            <span className="circle-loading">
                              <CircleLoadingIcon />
                            </span>
                          }
                          ariaLabel="Attach File Loading"
                          testId="attach-file-loading"
                          tooltipClassName="tooltip-sidebar"
                          buttonClassName="centered-icon-button"
                          placement="top-start"
                        />
                      ) : (
                        <Button
                          aria-label="Submit Prompt"
                          className={`submit-prompt prompt-input-button ${promptInput.trim().length > 0 && isPayloadReady ? 'active' : ''}`}
                          data-testid="submit-prompt"
                          onClick={submitPrompt}
                          disabled={
                            isLoading ||
                            isPromptError ||
                            !promptInput.trim().length ||
                            isAnyFileUploading ||
                            isChatDisabled ||
                            !isPayloadReady
                          }
                        >
                          <SubmitPromptIcon />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="chat-mode-container d-block d-md-none">
                {isPrivateChat && (
                  <Button
                    aria-label="Private Chat"
                    data-testid="private-chat"
                    onClick={togglePrivateChat}
                    disabled={isChatDisabled}
                  >
                    <PrivateChatIcon />
                  </Button>
                )}
                {chatAgent === 'Document' && (
                  <Button
                    aria-label="Add sources"
                    data-testid="add-sources"
                    onClick={() => handleAddSources()}
                    disabled={isChatDisabled}
                  >
                    <DocumentIcon />
                  </Button>
                )}
                {selectedModel?.name_of_model !== defaultModel?.name_of_model &&
                  chatAgent !== 'app-agent' && (
                    <Button
                      aria-label="select model"
                      data-testid="select-model"
                      onClick={() => setInitiatedModelSelect(true)}
                    >
                      <ModelSelectIcon />
                    </Button>
                  )}
                {selectedLanguage !== defaultLanguage && (
                  <Button
                    aria-label="select language"
                    data-testid="select-language"
                    onClick={() => setInitiatedLanguageSelect(true)}
                  >
                    <LanguageSelectIcon />
                  </Button>
                )}

                {(profitCenter?.label?.length > 0 || financeAppLocation?.label?.length > 0) && (
                  <Button
                    aria-label="finance-app"
                    data-testid="fiannce-app"
                    onClick={() => setInitiatedFinanceApp(true)}
                  >
                    <FinanceAppIcon />
                  </Button>
                )}
              </div>
            </div>
          </InputGroup>
          {isChatActive && (
            <div
              data-tesid="disclaimer-message"
              className="chat-input-protection-msg text-start text-md-center"
            >
              Elysia responses may be inaccurate. Know more about how your data is processed{' '}
              <Link
                to="https://portal.informa.com/sites/compliance/document/303301/Elysia-Notice-and-Acceptable-Use-Policy"
                target="_blank"
                className="custom-link"
              >
                here
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
});
ChatInput.displayName = 'ChatInput';

export default ChatInput;
